import { useTransform, motion } from 'framer-motion';

const C = {
  outline: '#4a9eff',
  bright:  '#7dd3fc',
  detail:  'rgba(74, 158, 255, 0.48)',
  dash:    'rgba(74, 158, 255, 0.2)',
  text:    '#2d5f88',
  fill:    'rgba(5, 14, 32, 0.6)',
};

// ── NETWORK TOPOLOGY ──────────────────────────────────────────────────────────
// 4 fully-connected layers: Input(3) → Hidden(4) → Hidden(4) → Output(2)
const LAYERS = [
  { id: 'in',  x: 62,  ys: [84, 150, 216],       r: 11 },
  { id: 'h1',  x: 154, ys: [72, 118, 164, 210],   r: 9  },
  { id: 'h2',  x: 248, ys: [72, 118, 164, 210],   r: 9  },
  { id: 'out', x: 338, ys: [114, 186],             r: 11 },
];
const LAYER_LABELS = ['INPUT', 'HIDDEN', 'HIDDEN', 'OUTPUT'];

function buildConns(a, b) {
  return a.ys.flatMap((y1, i) =>
    b.ys.map((y2, j) => ({
      key: `${a.id}${i}-${b.id}${j}`,
      x1: a.x, y1, x2: b.x, y2,
    }))
  );
}
const C01 = buildConns(LAYERS[0], LAYERS[1]); // 3×4 = 12
const C12 = buildConns(LAYERS[1], LAYERS[2]); // 4×4 = 16
const C23 = buildConns(LAYERS[2], LAYERS[3]); // 4×2 =  8

// A handful of weight annotations spread across the diagram
const WEIGHT_LABELS = [
  { x1: 62,  y1: 84,  x2: 154, y2: 72,  w: '0.82'  },
  { x1: 62,  y1: 150, x2: 154, y2: 164, w: '0.64'  },
  { x1: 154, y1: 118, x2: 248, y2: 72,  w: '-0.31' },
  { x1: 248, y1: 164, x2: 338, y2: 186, w: '0.91'  },
  { x1: 62,  y1: 216, x2: 154, y2: 210, w: '0.47'  },
];

// One highlighted forward-pass path: in[1] → h1[2] → h2[1] → out[0]
const PULSE_SEGS  = [
  { x1: 62,  y1: 150, x2: 154, y2: 164 },
  { x1: 154, y1: 164, x2: 248, y2: 118 },
  { x1: 248, y1: 118, x2: 338, y2: 114 },
];
const PULSE_NODES = [[62, 150], [154, 164], [248, 118], [338, 114]];

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────

// 5 dendrite branches radiating from node perimeter; seed → unique angles per node
function Dendrites({ cx, cy, r, seed }) {
  return Array.from({ length: 5 }, (_, i) => {
    const deg = seed + i * 71 + 9; // 71° spacing, offset by seed
    const rad = (deg * Math.PI) / 180;
    const len = 11 + (i % 3) * 3;
    const ox  = r + 1.5;
    return (
      <line
        key={i}
        x1={cx + Math.cos(rad) * ox}
        y1={cy + Math.sin(rad) * ox}
        x2={cx + Math.cos(rad) * (ox + len)}
        y2={cy + Math.sin(rad) * (ox + len)}
        stroke={C.detail} strokeWidth="0.75" strokeLinecap="round"
      />
    );
  });
}

// Full neuron: dendrites + halo ring + soma + inner detail
function Neuron({ cx, cy, r, seed }) {
  return (
    <g>
      <Dendrites cx={cx} cy={cy} r={r} seed={seed} />
      {/* Halo */}
      <circle cx={cx} cy={cy} r={r + 4.5}
        stroke={C.dash} strokeWidth="0.6" fill="none" />
      {/* Soma */}
      <circle cx={cx} cy={cy} r={r}
        stroke={C.outline} strokeWidth="1.5" fill={C.fill} />
      {/* Inner nucleus marker */}
      <circle cx={cx} cy={cy} r={r * 0.32}
        stroke={C.detail} strokeWidth="0.7" fill="none" />
    </g>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function NeuralNet({ scrollProgress }) {
  // Assembly stages ─ each layer of content fades in as you scroll
  const nodesOp   = useTransform(scrollProgress, [0.00, 0.15], [0, 1]); // all neurons
  const conn01Op  = useTransform(scrollProgress, [0.14, 0.28], [0, 1]); // input → hidden1
  const conn12Op  = useTransform(scrollProgress, [0.28, 0.42], [0, 1]); // hidden1 → hidden2
  const conn23Op  = useTransform(scrollProgress, [0.41, 0.55], [0, 1]); // hidden2 → output
  const labelsOp  = useTransform(scrollProgress, [0.53, 0.67], [0, 1]); // labels + title
  const weightsOp = useTransform(scrollProgress, [0.65, 0.79], [0, 1]); // edge weights
  const pulseOp   = useTransform(scrollProgress, [0.77, 0.91], [0, 1]); // forward-pass pulse

  return (
    <svg
      viewBox="0 0 400 300"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '100%' }}
    >

      {/* ── CONNECTIONS: input → hidden 1 ── */}
      <motion.g style={{ opacity: conn01Op }}>
        {C01.map(c => (
          <line key={c.key}
            x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2}
            stroke={C.detail} strokeWidth="0.65" />
        ))}
      </motion.g>

      {/* ── CONNECTIONS: hidden 1 → hidden 2 ── */}
      <motion.g style={{ opacity: conn12Op }}>
        {C12.map(c => (
          <line key={c.key}
            x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2}
            stroke={C.detail} strokeWidth="0.65" />
        ))}
      </motion.g>

      {/* ── CONNECTIONS: hidden 2 → output ── */}
      <motion.g style={{ opacity: conn23Op }}>
        {C23.map(c => (
          <line key={c.key}
            x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2}
            stroke={C.detail} strokeWidth="0.65" />
        ))}
      </motion.g>

      {/* ── ALL NEURONS (organic sketch phase, then connections join them) ── */}
      <motion.g style={{ opacity: nodesOp }}>
        {LAYERS.flatMap((layer, li) =>
          layer.ys.map((y, ni) => (
            <Neuron
              key={`${layer.id}-${ni}`}
              cx={layer.x} cy={y} r={layer.r}
              seed={li * 53 + ni * 37}
            />
          ))
        )}
      </motion.g>

      {/* ── LABELS + TITLE BLOCK ── */}
      <motion.g style={{ opacity: labelsOp }}>
        {LAYERS.map((layer, li) => {
          const topY = Math.min(...layer.ys);
          const botY = Math.max(...layer.ys);
          return (
            <g key={`lbl-${layer.id}`}>
              {/* Layer type label above first node */}
              <text x={layer.x} y={topY - 16}
                textAnchor="middle" fill={C.text}
                fontSize="6.5" fontFamily="'Space Mono', monospace"
                letterSpacing="0.12em">
                {LAYER_LABELS[li]}
              </text>
              {/* Node index numbers inside nodes */}
              {layer.ys.map((y, ni) => (
                <text key={ni} x={layer.x} y={y + 2.5}
                  textAnchor="middle" fill={C.text}
                  fontSize="5.5" fontFamily="'Space Mono', monospace">
                  {ni + 1}
                </text>
              ))}
              {/* Node count below last node */}
              <text x={layer.x} y={botY + 19}
                textAnchor="middle" fill={C.text}
                fontSize="5.5" fontFamily="'Space Mono', monospace">
                ×{layer.ys.length}
              </text>
            </g>
          );
        })}

        {/* Title block — bottom-left */}
        <rect x="4" y="246" width="140" height="47"
          stroke={C.detail} strokeWidth="0.8" fill="none" />
        <line x1="4"   y1="255" x2="144" y2="255" stroke={C.detail} strokeWidth="0.5" />
        <line x1="4"   y1="263" x2="144" y2="263" stroke={C.detail} strokeWidth="0.35" />
        <text x="8" y="253" fill={C.text} fontSize="5.8" fontFamily="'Space Mono', monospace">NEURAL NET ASSY</text>
        <text x="8" y="261" fill={C.text} fontSize="5"   fontFamily="'Space Mono', monospace">DWG: AS-2026-002</text>
        <text x="8" y="270" fill={C.text} fontSize="5"   fontFamily="'Space Mono', monospace">ARCH: 3-4-4-2  REV: A</text>
        <text x="8" y="278" fill={C.text} fontSize="5"   fontFamily="'Space Mono', monospace">DESIGNER: A. SHAKER</text>
        <text x="8" y="286" fill={C.text} fontSize="5"   fontFamily="'Space Mono', monospace">DATE: 2026</text>

        {/* Arch type label — bottom-right */}
        <text x="396" y="292" textAnchor="end" fill={C.text}
          fontSize="5.5" fontFamily="'Space Mono', monospace" letterSpacing="0.06em">
          FULLY CONNECTED FEEDFORWARD
        </text>
      </motion.g>

      {/* ── EDGE WEIGHT ANNOTATIONS ── */}
      <motion.g style={{ opacity: weightsOp }}>
        {WEIGHT_LABELS.map((w, i) => {
          const mx = (w.x1 + w.x2) / 2;
          const my = (w.y1 + w.y2) / 2;
          return (
            <text key={i} x={mx} y={my - 3}
              textAnchor="middle" fill={C.text}
              fontSize="5.5" fontFamily="'Space Mono', monospace">
              {w.w}
            </text>
          );
        })}
      </motion.g>

      {/* ── FORWARD PASS PULSE ─── highlighted path + pulsing nodes ── */}
      <motion.g style={{ opacity: pulseOp }}>
        {PULSE_SEGS.map((seg, i) => (
          <line key={i}
            x1={seg.x1} y1={seg.y1} x2={seg.x2} y2={seg.y2}
            stroke={C.bright} strokeWidth="2" strokeLinecap="round"
            className="bp-pulse-line"
          />
        ))}
        {PULSE_NODES.map(([cx, cy], i) => (
          <circle key={i}
            cx={cx} cy={cy} r="9"
            stroke={C.bright} strokeWidth="1.5" fill="none"
            className="bp-pulse-node"
          />
        ))}
      </motion.g>

    </svg>
  );
}
